import type { MemberRole, MyTenant, TenantAccessVia } from '@warehouselogix/shared';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorBar, LoadingState } from '../components/ScreenChrome';

import { findTenantById, findTenantBySlug, useMyTenants, writeLastTenant } from './useMyTenants';

/**
 * The tenant every `/admin/t/:tenantId/*` call is made against (tenancy v1).
 *
 * Resolved from the route's `:slug` (or an explicit `tenantId` for the
 * platform console's `/tenants/:tenantId/members` mount) against
 * `GET /me/tenants`. Unknown / not-listed → back to the picker with
 * "You don't have access to '{slug}'". A 403 from ANY admin call reloads the
 * list; if the tenant has vanished (grant revoked) the same path bounces the
 * user out. Server stays authoritative — this only shapes the UI.
 */
export interface CurrentTenant {
  tenant: MyTenant;
  role: MemberRole;
  via: TenantAccessVia;
  /** Wired into the admin HTTP layer: a 403 re-checks memberships. */
  onForbidden: (message: string) => void;
}

const CurrentTenantContext = createContext<CurrentTenant | null>(null);

export interface CurrentTenantProviderProps {
  /**
   * Resolve by tenant UUID instead of the `:slug` route param (platform
   * console mount). Platform admins see every tenant in `me/tenants`, so the
   * lookup succeeds for them without a membership grant.
   */
  tenantId?: string;
  children: ReactNode;
}

/** Location state carried to the picker when a tenant cannot be entered. */
export interface PickerState {
  message?: string;
  /** True when the user deliberately asked for the picker (suppresses auto-redirects). */
  explicit?: boolean;
}

export function CurrentTenantProvider({
  tenantId,
  children,
}: CurrentTenantProviderProps): JSX.Element | null {
  const params = useParams<{ slug: string }>();
  const my = useMyTenants();
  const navigate = useNavigate();
  const key = tenantId ?? params.slug ?? '';
  const bySlug = tenantId === undefined;

  const tenant = useMemo<MyTenant | null>(() => {
    if (my.data === null || key === '') {
      return null;
    }
    return bySlug ? findTenantBySlug(my.data, key) : findTenantById(my.data, key);
  }, [my.data, key, bySlug]);

  // "Settled" = the list is loaded and no reload is in flight. During a
  // reload the previous list (and tenant) stay in place, so a 403-triggered
  // re-check only bounces the user once the fresh answer says they are gone.
  const settled = !my.loading && my.data !== null;

  useEffect(() => {
    if (!settled) {
      return;
    }
    if (tenant !== null) {
      if (bySlug) {
        writeLastTenant(tenant.slug);
      }
      return;
    }
    const state: PickerState = { message: `You don't have access to '${key}'` };
    void navigate('/', { replace: true, state });
  }, [settled, tenant, bySlug, key, navigate]);

  const reload = my.reload;
  const onForbidden = useCallback((): void => {
    // Membership may have changed under us; re-check. If this tenant is no
    // longer listed, the effect above sends the user to the picker.
    reload();
  }, [reload]);

  const value = useMemo<CurrentTenant | null>(
    () => (tenant === null ? null : { tenant, role: tenant.role, via: tenant.via, onForbidden }),
    [tenant, onForbidden]
  );

  if (value === null) {
    if (my.error !== null && my.data === null) {
      return <ErrorBar message={my.error} onRetry={my.reload} />;
    }
    if (settled) {
      // Not listed: the effect is navigating to the picker.
      return null;
    }
    return <LoadingState label="Loading your tenants…" />;
  }

  return <CurrentTenantContext.Provider value={value}>{children}</CurrentTenantContext.Provider>;
}

/** Inside a tenant route only. Throws outside the provider — a wiring bug, not a runtime state. */
export function useCurrentTenant(): CurrentTenant {
  const ctx = useContext(CurrentTenantContext);
  if (ctx === null) {
    throw new Error(
      'useCurrentTenant()/useAdminApi() must be used under a CurrentTenantProvider (/t/:slug/*).'
    );
  }
  return ctx;
}
